<template>
  <PaymentCardBase
    :title="title"
    :active="active"
    :key="`card-${cardsList.length}`"
  >
    <template v-slot:body>
      <section class="p-5 border-t border-b" style="width: 100%;">
        <splide :options="splideOption" @splide:move="onMove">
          <splide-slide v-for="(card, cardIndex) in cardsList" :key="cardIndex">
            <credit-card
              :name="fullName"
              :number="card.number"
              :brand="card.brand"
              :expiry="card.expiry"
            />
          </splide-slide>
        </splide>
      </section>

      <section>
        <feature-list-typography
          :text-left="
            $t(
              'components.riderDetailsManagement.diveIntoTab.paymentMethod.card.totalAmountSpent'
            )
          "
          :text-right="
            `${currencySymbol} ${Number(
              getCurrentCard.payment_summary.total_amount || 0
            ).toFixed(2)}`
          "
        />

        <feature-list-typography
          :text-left="
            $t(
              'components.riderDetailsManagement.diveIntoTab.paymentMethod.card.totalTripPayment'
            )
          "
          :text-right="
            `${currencySymbol} ${Number(
              getCurrentCard.payment_summary.total_trip_payment
            ).toFixed(2)}`
          "
        />

        <feature-list-typography
          :text-left="
            $t(
              'components.riderDetailsManagement.diveIntoTab.paymentMethod.card.totalPassPayment'
            )
          "
          :text-right="
            `${currencySymbol} ${Number(
              getCurrentCard.payment_summary.total_pass_payment
            ).toFixed(2)}`
          "
        />

        <feature-list-typography
          :text-left="
            $t(
              'components.riderDetailsManagement.diveIntoTab.paymentMethod.card.totalTopupPayment'
            )
          "
          :text-right="
            `${currencySymbol} ${Number(
              getCurrentCard.payment_summary.total_topup_payment
            ).toFixed(2)}`
          "
        />

        <feature-list-typography
          :text-left="
            $t(
              'components.riderDetailsManagement.diveIntoTab.paymentMethod.card.totalRefunded'
            )
          "
          :text-right="
            `${currencySymbol} ${Number(
              getCurrentCard.payment_summary.total_refunded
            ).toFixed(2)}`
          "
        />
      </section>
      <section
        class="flex flex-col items-center justify-center w-full py-4 mb-4 lg:flex-row lg:gap-x-3 gap-y-3 lg:gap-y-0"
      >
        <AnchorButton
          :text="
            $t(
              'components.riderDetailsManagement.diveIntoTab.paymentMethod.card.deleteCard'
            )
          "
          :width="`10rem`"
          :variant="`warning`"
          @click="askConfirmation"
        />
        <ConfirmFirst
          :id="`confirmFirst-${getCurrentCard.id}`"
          :key="`confirmFirst-${getCurrentCard.id}`"
          :isLoading="isCardDeleteLoading"
          :title="
            `${$t(
              'components.riderDetailsManagement.diveIntoTab.paymentMethod.card.deleteCard'
            )} ${getCurrentCard.brand}**${getCurrentCard.number}?`
          "
          :confirmButtonText="$t('components.stepNavigation.delete')"
          :confirmButtonVariant="`warning-alt`"
          @confirm="handleDeleteCard(getCurrentCard.id)"
          @cancel="handleCancellation"
        />
      </section>
    </template>
  </PaymentCardBase>
</template>

<script>
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import '@splidejs/splide/dist/css/themes/splide-default.min.css'
import { useEndpoints } from '@/composables'

export default {
  name: 'PaymentCardCreditBlock',
  title: 'Cards',
  components: {
    Splide,
    SplideSlide,
    CreditCard: () => import('@/components/cards/CreditCard.vue'),
    PaymentCardBase: () => import('./PaymentCardBaseBlock'),
    FeatureListTypography: () =>
      import('@/components/typography/FeatureListTypography.vue'),
    AnchorButton: () => import('@/components/form/AnchorButton.vue'),
    ConfirmFirst: () => import('@/components/modals/ConfirmFirst'),
  },
  props: {
    title: {
      type: [String, Number],
      required: true,
    },
    active: {
      type: Boolean,
      required: false,
      default: false,
    },
    currencySymbol: {
      type: String,
      required: false,
      default: '$',
    },
    totalDeposited: {
      type: [Number, String],
      required: false,
      default: 0,
    },
    totalSpent: {
      type: [Number, String],
      required: false,
      default: 0,
    },
    totalTrips: {
      type: [Number, String],
      required: false,
      default: 0,
    },
    fullName: {
      type: String,
      required: false,
    },
    cards: {
      type: Array,
      requied: false,
    },
  },

  data() {
    return {
      splideOption: {
        rewind: true,
        width: '100%',
        fixedWidth: '100%',
        perPage: 1,
        // gap: "1rem"
        /*  padding: {
          right: "0.25rem",
          left: "0rem"
        } */
      },
      currentSlide: 0,
      cardsList: [],
      isCardDeleteLoading: false,
    }
  },

  computed: {
    getCurrentCard() {
      return this.cardsList[this.currentSlide]
    },
  },
  watch: {
    cards: {
      immediate: true,
      deep: true,
      handler(data) {
        this.cardsList = data
      },
    },
  },

  methods: {
    askConfirmation() {
      this.$modal.show(`confirmFirst-${this.getCurrentCard.id}`)
    },
    onMove(_e, newIndex) {
      // console.log('onMove', newIndex)
      this.currentSlide = newIndex
    },
    handleDeleteCard(cardId) {
      this.isCardDeleteLoading = true

      this.$http
        .delete(useEndpoints.rider.deleteCard(cardId))
        .then(() => {
          this.$emit('card-deleted')
          this.$notify(
            {
              group: 'bottomRight',
              type: 'success',
              title: 'Success',
              text: `Card deleted successfully.`,
            },
            3000
          )
        })
        .catch((err) => {
          this.$notify(
            {
              group: 'bottomRight',
              type: 'error',
              title: 'Failed',
              text: `${err.response.data.message || 'Failed to delete card.'}`,
            },
            5000
          )
          console.error('Error deleting card:', { err })
        })
        .finally(() => {
          this.isCardDeleteLoading = false
          this.$modal.hide(`confirmFirst-${this.getCurrentCard.id}`)
        })
    },
    handleCancellation() {
      this.$modal.hide(`confirmFirst-${this.getCurrentCard.id}`)
    },
  },
}
</script>

<style lang="scss" scoped></style>
